<template>
  <section class="website-packages">
    <div class="container">
      <div class="header-group reveal">
        <h2 class="section-title">Our Website Packages</h2>
        <p class="section-subtitle">
          Professional websites that grow with your business
        </p>
        <div class="accent-line"></div>
      </div>

      <div class="packages-grid">
        <!-- Single Page Package -->
        <div class="package-card single-page reveal delay-1">
          <div class="package-header">
            <span class="package-icon">🚀</span>
            <h3>Single Page</h3>
            <div class="package-badge starter">Perfect Start</div>
          </div>

          <div class="pricing-section">
            <div class="price-main">
              <span class="price-label">Starting from</span>
              <div class="price-amount">
                <span class="currency">$</span>
                <span class="amount">499</span>
              </div>
            </div>
            <div class="hosting-price">+ $160/year hosting</div>
          </div>

          <ul class="features-list">
            <li>
              <span class="check">✓</span>
              <span>Custom design</span>
            </li>
            <li>
              <span class="check">✓</span>
              <span>Mobile-responsive</span>
            </li>
            <li>
              <span class="check">✓</span>
              <span>Contact forms</span>
            </li>
            <li>
              <span class="check">✓</span>
              <span>SEO basics</span>
            </li>
          </ul>

          <div class="timeline">
            <span class="timeline-icon">⏱️</span>
            <span>7-14 days delivery</span>
          </div>

          <a href="mailto:contact@bizzento.com" class="package-cta">Get Started</a>
        </div>

        <!-- Multi-Page Package -->
        <div class="package-card multi-page reveal delay-2">
          <div class="package-header">
            <span class="package-icon">📈</span>
            <h3>Multi-Page</h3>
            <div class="package-badge premium">Most Popular</div>
          </div>

          <div class="pricing-section">
            <div class="price-main">
              <span class="price-label">Starting from</span>
              <div class="price-amount">
                <span class="currency">$</span>
                <span class="amount">899</span>
              </div>
            </div>
            <div class="hosting-price">+ $160/year hosting</div>
          </div>

          <ul class="features-list">
            <li>
              <span class="check">✓</span>
              <span>Up to 5 pages</span>
            </li>
            <li>
              <span class="check">✓</span>
              <span>Advanced integrations</span>
            </li>
            <li>
              <span class="check">✓</span>
              <span>SEO optimization</span>
            </li>
            <li>
              <span class="check">✓</span>
              <span>Analytics setup</span>
            </li>
          </ul>

          <div class="timeline">
            <span class="timeline-icon">⏱️</span>
            <span>2-3 weeks delivery</span>
          </div>

          <a href="mailto:contact@bizzento.com" class="package-cta featured">Get Started</a>
        </div>
      </div>

      <!-- Pricing Note -->
      <div class="pricing-note reveal delay-3">
        <div class="note-card">
          <span class="note-icon">💡</span>
          <p>
            <strong>Custom pricing tailored to you.</strong> Final price depends
            on design complexity, custom features, and integrations. Every
            project gets a detailed quote after your
            <strong>free consultation</strong>.
          </p>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.website-packages {
  /* Theme Variables */
  --bg-warm-base: #f2eee0;
  --sunlight-glow: #faefca;
  --soft-gold: #f5e5b0;
  --dark-accent: #1a1a1a;
  --text-muted: #8e8a82;
  --gold-highlight: #d4af37;
  --success-green: #2d6a4f;

  padding: 100px 24px;
  background: white;
  color: var(--dark-accent);
}

.container {
  max-width: 1200px;
  margin: 0 auto;
}

/* Header */
.header-group {
  text-align: center;
  margin-bottom: 80px;
}

.section-title {
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 800;
  margin-bottom: 12px;
  letter-spacing: -0.03em;
}

.section-subtitle {
  color: var(--text-muted);
  font-size: 1.1rem;
  margin-bottom: 20px;
}

.accent-line {
  width: 80px;
  height: 4px;
  background: var(--soft-gold);
  margin: 0 auto;
  border-radius: 2px;
}

/* Packages Grid */
.packages-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 40px;
  margin-bottom: 60px;
}

/* Package Card */
.package-card {
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(10px);
  border: 2px solid #e8dfd5;
  border-radius: 32px;
  padding: 48px 40px;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  position: relative;
  overflow: hidden;
}

.package-card:hover {
  transform: translateY(-8px);
  border-color: var(--gold-highlight);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.08);
}

/* Featured Multi-Page Card */
.package-card.multi-page {
  background: linear-gradient(
    135deg,
    rgba(250, 239, 202, 0.3) 0%,
    rgba(255, 255, 255, 0.6) 100%
  );
  border-color: var(--gold-highlight);
}

.package-card.multi-page:hover {
  box-shadow: 0 25px 50px rgba(212, 175, 55, 0.15);
}

/* Package Header */
.package-header {
  text-align: center;
  margin-bottom: 32px;
  position: relative;
}

.package-icon {
  font-size: 3rem;
  display: block;
  margin-bottom: 16px;
}

.package-header h3 {
  font-size: 1.75rem;
  font-weight: 800;
  margin-bottom: 12px;
  letter-spacing: -0.02em;
}

.package-badge {
  display: inline-block;
  padding: 6px 16px;
  border-radius: 100px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.package-badge.starter {
  background: #e7f3ee;
  color: var(--success-green);
}

.package-badge.premium {
  background: var(--sunlight-glow);
  color: #856404;
  border: 1px solid var(--gold-highlight);
}

/* Pricing Section */
.pricing-section {
  text-align: center;
  margin-bottom: 32px;
  padding-bottom: 32px;
  border-bottom: 2px solid rgba(74, 55, 40, 0.08);
}

.price-label {
  display: block;
  font-size: 0.875rem;
  color: var(--text-muted);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 8px;
}

.price-amount {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 4px;
  margin-bottom: 12px;
}

.currency {
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-muted);
  margin-top: 8px;
}

.amount {
  font-size: 4rem;
  font-weight: 900;
  line-height: 1;
  letter-spacing: -0.02em;
  color: var(--dark-accent);
}

.hosting-price {
  font-size: 0.938rem;
  color: var(--text-muted);
  font-weight: 500;
}

/* Features List */
.features-list {
  list-style: none;
  padding: 0;
  margin: 0 0 32px 0;
}

.features-list li {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  font-size: 1rem;
  line-height: 1.5;
}

.check {
  color: var(--gold-highlight);
  font-weight: 700;
  font-size: 1.1rem;
  flex-shrink: 0;
}

/* Timeline */
.timeline {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 20px;
  background: rgba(250, 239, 202, 0.4);
  border-radius: 100px;
  margin-bottom: 32px;
  font-size: 0.938rem;
  font-weight: 600;
  color: #856404;
}

.timeline-icon {
  font-size: 1.1rem;
}

/* CTA Button */
.package-cta {
  display: block;
  text-align: center;
  padding: 18px 32px;
  background: var(--dark-accent);
  color: white;
  border-radius: 12px;
  text-decoration: none;
  font-weight: 700;
  font-size: 1.0625rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.package-cta:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
}

.package-cta.featured {
  background: linear-gradient(135deg, #d4af37 0%, #bfa14d 100%);
  color: white;
}

.package-cta.featured:hover {
  box-shadow: 0 8px 24px rgba(212, 175, 55, 0.4);
}

/* Pricing Note */
.pricing-note {
  max-width: 900px;
  margin: 0 auto;
}

.note-card {
  background: linear-gradient(
    135deg,
    rgba(250, 239, 202, 0.4) 0%,
    rgba(245, 229, 176, 0.3) 100%
  );
  border: 1px dashed var(--gold-highlight);
  border-radius: 24px;
  padding: 32px 40px;
  display: flex;
  align-items: flex-start;
  gap: 20px;
}

.note-icon {
  font-size: 2rem;
  flex-shrink: 0;
}

.note-card p {
  margin: 0;
  font-size: 1.0625rem;
  line-height: 1.7;
  color: var(--dark-accent);
}

.note-card strong {
  font-weight: 700;
  color: #856404;
}

/* Responsive Design */
@media (max-width: 768px) {
  .website-packages {
    padding: 60px 20px;
  }

  .header-group {
    margin-bottom: 60px;
  }

  .packages-grid {
    grid-template-columns: 1fr;
    gap: 30px;
  }

  .package-card {
    padding: 40px 32px;
  }

  .amount {
    font-size: 3.5rem;
  }

  .note-card {
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 24px 20px;
  }

  .note-icon {
    font-size: 2.5rem;
  }
}

@media (max-width: 480px) {
  .package-card {
    padding: 32px 24px;
  }

  .package-header h3 {
    font-size: 1.5rem;
  }

  .amount {
    font-size: 3rem;
  }

  .features-list li {
    font-size: 0.938rem;
  }
}
</style>

<script>
export default {
  name: 'WebsitePackagesSection',
};
</script>
